import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";

/**
 * Founder-only historical margin chart — plots every past cohort's
 * margin_pct + grand_total so pricing drift is obvious at a glance.
 */
export default function MarginHistoryChart() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/weekend-cohort-organizer-history")
      .then(r => setRows(r.data || []))
      .catch(() => setErr(true));
  }, []);

  const chartData = useMemo(() => rows.filter(r => r.revenue > 0).map(r => ({
    x: r.tour_date + (r.destination ? ` · ${r.destination}` : ""),
    "Margin %": r.margin_pct,
    "Threshold %": r.threshold_pct || null,
    grand: r.grand_total,
    revenue: r.revenue,
    pax: r.pax,
    cohort_key: r.cohort_key,
  })), [rows]);

  const stats = useMemo(() => {
    if (!chartData.length) return null;
    const pcts = chartData.map(d => d["Margin %"]);
    const avg = pcts.reduce((a, b) => a + b, 0) / pcts.length;
    const last = pcts[pcts.length - 1];
    const first = pcts[0];
    return { avg, last, first, trend: last - first };
  }, [chartData]);

  if (err) return null;

  return (
    <div className="aii-card p-5" data-testid="margin-history-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Weekend cohort history</div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            {stats && stats.trend >= 0 ? <TrendingUp className="text-emerald-600" size={18}/> : <TrendingDown className="text-red-600" size={18}/>}
            Historical margin — {chartData.length} cohort{chartData.length === 1 ? "" : "s"}
          </h3>
        </div>
        {stats && (
          <div className="flex items-center gap-3 text-xs">
            <div className="border border-[color:var(--aii-border)] rounded px-2 py-1">
              <div className="text-[color:var(--aii-muted)] uppercase text-[10px]">Avg margin</div>
              <div className="font-semibold text-brand">{stats.avg.toFixed(1)}%</div>
            </div>
            <div className="border border-[color:var(--aii-border)] rounded px-2 py-1">
              <div className="text-[color:var(--aii-muted)] uppercase text-[10px]">Latest</div>
              <div className={`font-semibold ${stats.last < 20 ? "text-red-600" : "text-emerald-700"}`}>{stats.last.toFixed(1)}%</div>
            </div>
            <div className="border border-[color:var(--aii-border)] rounded px-2 py-1">
              <div className="text-[color:var(--aii-muted)] uppercase text-[10px]">Trend</div>
              <div className={`font-semibold ${stats.trend >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                {stats.trend >= 0 ? "+" : ""}{stats.trend.toFixed(1)} pts
              </div>
            </div>
          </div>
        )}
      </div>

      {chartData.length === 0 ? (
        <div className="text-center text-[color:var(--aii-muted)] py-8" data-testid="margin-history-empty">
          No priced weekend cohorts yet. Once you add tourists with revenue on the Weekend Trip Organizer, this chart will populate.
        </div>
      ) : (
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
              <CartesianGrid stroke="#E5EBE9" strokeDasharray="3 3" />
              <XAxis dataKey="x" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={60} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} unit="%" />
              <Tooltip
                formatter={(v, k) => [`${Number(v).toFixed(1)}%`, k]}
                contentStyle={{ fontSize: 11 }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#dc2626" strokeDasharray="3 3" label={{ value: "Break-even", fontSize: 9, fill: "#dc2626", position: "insideBottomRight" }} />
              <Line type="monotone" dataKey="Margin %" stroke="#1B7A3E" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="Threshold %" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
